import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnHeaderFilter } from './ColumnHeaderFilter';

function isSortableColumn(column) {
  if (!column?.header) return false;
  if (column.sortable === false) return false;
  if (column.key === 'actions' || column.key === 'action') return false;
  return true;
}

function sortValueOf(column, row) {
  if (typeof column.sortValue === 'function') return column.sortValue(row);
  const raw = row?.[column.key];
  if (raw == null || typeof raw === 'object') return '';
  return raw;
}

function compareValues(left, right) {
  if (left === '' && right === '') return 0;
  if (left === '') return 1;
  if (right === '') return -1;
  if (typeof left === 'number' && typeof right === 'number') return left - right;
  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

function hasColumnFilter(column) {
  return Boolean(column?.filter && typeof column.filter.onChange === 'function');
}

function columnSortId(column) {
  return column?.sortId || column?.key;
}

function SelectionCheckbox({ checked, onChange, label, disabled = false, title, inputRef }) {
  return (
    <input
      ref={inputRef}
      type="checkbox"
      className="h-4 w-4 accent-[#003B5B]"
      checked={checked}
      disabled={disabled}
      aria-label={label}
      title={title || undefined}
      onClick={(event) => event.stopPropagation()}
      onChange={(event) => {
        event.stopPropagation();
        if (disabled) return;
        onChange(event.target.checked);
      }}
    />
  );
}

/**
 * Shared table chrome (Figma UMB eVault 282:48776).
 * `columns`: [{ key, header, render?, align?, className?, headerClassName?, sortable?, sortValue?, sortId?, filter? }]
 * `filter` is optional: { type: 'select'|'text', value, onChange, options?, placeholder?, ariaLabel? }
 * A column with `filter` opens the funnel as a filter. Other sortable columns
 * open it as A–Z / Z–A. The funnel applies only on Submit.
 * Pass `onSortChange` to own sort (server-side). Omit it to keep in-page sorting.
 */
export function DataTable({
  columns,
  rows = [],
  rowKey,
  rowHref,
  onRowClick,
  isSelected,
  loading = false,
  emptyMessage = 'No records found.',
  minWidth = 720,
  sortKey: sortKeyProp,
  sortDir: sortDirProp,
  onSortChange,
  selection,
}) {
  const navigate = useNavigate();
  const tableId = useId();
  const selectAllRef = useRef(null);
  const colCount = columns.length + (selection ? 1 : 0);
  const [internalSortKey, setInternalSortKey] = useState('');
  const [internalSortDir, setInternalSortDir] = useState('asc');
  const [openMenuKey, setOpenMenuKey] = useState('');
  const controlled = typeof onSortChange === 'function';
  const sortKey = controlled ? sortKeyProp || '' : internalSortKey;
  const sortDir = controlled ? sortDirProp || 'asc' : internalSortDir;

  const displayRows = useMemo(() => {
    const tableRows = Array.isArray(rows) ? rows : [];
    if (controlled || !sortKey || loading) return tableRows;
    const column = columns.find((item) => item.key === sortKey);
    if (!column) return tableRows;
    const next = [...tableRows];
    next.sort((left, right) => {
      const compared = compareValues(sortValueOf(column, left), sortValueOf(column, right));
      return sortDir === 'desc' ? -compared : compared;
    });
    return next;
  }, [columns, controlled, loading, rows, sortDir, sortKey]);

  const selectableRows = selection
    ? displayRows.filter((row) => !selection.isDisabled?.(row))
    : [];
  const selectedOnPage = selection
    ? selectableRows.filter((row) => selection.isSelected(row)).length
    : 0;
  const allPageSelected = selectableRows.length > 0 && selectedOnPage === selectableRows.length;
  const somePageSelected = selectedOnPage > 0 && !allPageSelected;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = somePageSelected;
    }
  }, [somePageSelected]);

  function applySort(column, dir) {
    if (!isSortableColumn(column) || hasColumnFilter(column)) return;
    const nextDir = dir === 'desc' ? 'desc' : 'asc';
    if (controlled) {
      onSortChange(columnSortId(column), nextDir);
      return;
    }
    setInternalSortKey(column.key);
    setInternalSortDir(nextDir);
  }

  // `minWidth` is a layout hint, never wider than the container: the table fits
  // the card and cells wrap, so a page shows one scrollbar (the page's own)
  // instead of a horizontal bar under every table. Only when the viewport is
  // narrower than the table's intrinsic content does overflow-x-auto kick in.
  const tableMinWidth = minWidth ? `min(100%, ${Number(minWidth)}px)` : undefined;

  return (
    <div className="overflow-hidden rounded-2xl border border-[#D8D4D4] bg-white">
      <div className="overflow-x-auto">
        <table
          className="w-full border-collapse text-left text-[14px] leading-5 text-[#1F2933]"
          style={tableMinWidth ? { minWidth: tableMinWidth } : undefined}
        >
          <thead>
            <tr className="bg-[#003B5B]">
              {selection ? (
                <th className="w-12 border-r border-[#003A70] px-3 py-2.5">
                  <SelectionCheckbox
                    inputRef={selectAllRef}
                    checked={allPageSelected}
                    disabled={loading || selectableRows.length === 0}
                    label="Select all packages on this page"
                    onChange={(checked) => selection.onToggleAll(selectableRows, checked)}
                  />
                </th>
              ) : null}
              {columns.map((col) => {
                const filtered = hasColumnFilter(col);
                const sortable = isSortableColumn(col) && !filtered;
                const active = sortable && sortKey === columnSortId(col);
                const ariaSort = !sortable
                  ? undefined
                  : active
                    ? sortDir === 'desc'
                      ? 'descending'
                      : 'ascending'
                    : 'none';
                const headerId = `${tableId}-${col.key}-label`;

                return (
                  <th
                    key={col.key}
                    aria-sort={ariaSort}
                    aria-labelledby={col.header ? headerId : undefined}
                    className={`border-r border-[#003A70] px-4 py-2.5 last:border-r-0 ${
                      col.align === 'right' ? 'text-right' : 'text-left'
                    } ${col.headerClassName || ''}`}
                  >
                    {col.header ? (
                      <div className="inline-flex items-center gap-2.5">
                        <span id={headerId} className="text-[14px] font-bold leading-5 text-white">
                          {col.header}
                        </span>
                        {filtered || sortable ? (
                          <ColumnHeaderFilter
                            header={col.header}
                            mode={filtered ? 'filter' : 'sort'}
                            filter={col.filter}
                            sortDir={active ? sortDir : ''}
                            open={openMenuKey === col.key}
                            onOpenChange={(next) => setOpenMenuKey(next ? col.key : '')}
                            onApplySort={(dir) => applySort(col, dir)}
                          />
                        ) : null}
                      </div>
                    ) : null}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={colCount}
                  className="border border-[#F1F1F1] px-4 py-10 text-center text-sm text-[#5A5A5A]"
                >
                  Loading…
                </td>
              </tr>
            ) : displayRows.length ? (
              displayRows.map((row) => {
                const href = onRowClick ? null : rowHref?.(row) || null;
                const selected = Boolean(isSelected?.(row) || selection?.isSelected(row));
                const clickable = Boolean(href || onRowClick);
                const activate = () => {
                  if (onRowClick) onRowClick(row);
                  else if (href) navigate(href);
                };
                const rowDisabled = Boolean(selection?.isDisabled?.(row));
                return (
                  <tr
                    key={rowKey(row)}
                    onClick={clickable ? activate : undefined}
                    onKeyDown={
                      clickable
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              activate();
                            }
                          }
                        : undefined
                    }
                    tabIndex={clickable ? 0 : undefined}
                    aria-selected={isSelected || selection ? selected : undefined}
                    className={
                      selected
                        ? 'bg-sky-50/80'
                        : clickable
                          ? 'cursor-pointer hover:bg-[#F8FAFC] focus:bg-[#F8FAFC] focus:outline-none'
                          : ''
                    }
                  >
                    {selection ? (
                      <td className="h-[57px] border border-[#F1F1F1] px-3 py-4 align-middle">
                        <SelectionCheckbox
                          checked={selected}
                          disabled={rowDisabled}
                          title={
                            rowDisabled && selection.disabledReason
                              ? selection.disabledReason(row)
                              : undefined
                          }
                          label={selection.label ? selection.label(row) : 'Select package'}
                          onChange={() => selection.onToggle(row)}
                        />
                      </td>
                    ) : null}
                    {columns.map((col, index) => (
                      <td
                        key={col.key}
                        className={`h-[57px] border border-[#F1F1F1] px-4 py-4 align-middle ${
                          index === 0 ? 'font-semibold' : ''
                        } ${col.align === 'right' ? 'text-right' : 'text-left'} ${
                          col.className || ''
                        }`}
                      >
                        {col.render ? col.render(row) : row[col.key]}
                      </td>
                    ))}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={colCount}
                  className="border border-[#F1F1F1] px-4 py-10 text-center text-sm text-[#5A5A5A]"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
